import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";

import AdminLayoutWrapper from "@components/layout/AdminLayoutWrapper";
import { PaymentInfo } from "@data-types/payment-info";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";
import { useRootAppStore } from "@hooks";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import { redirect } from "@state";
import { getCurrentCulture, setTabTitle } from "@utils";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { logger } from "@utils/logger/logger";
import { formatDateTime } from "@utils/offer";
import classNames from "classnames";
import { DateTime } from "luxon";
import { observer } from "mobx-react";

export default observer(function PaymentInfoPage() {
	const { t } = useTranslation();
	const [transactionDetails, setTransactionDetails] = useState<PaymentInfo>(
		{} as PaymentInfo
	);

	const rootStore = useRootAppStore();

	const location = useLocation();

	useScrollRestoration({ type: ConfigType.Basic });

	useEffect(() => {
		setTabTitle(t("PAYMENT_INFO.PAYMENT_DETAILS"));
		const params = new URLSearchParams(location.search);
		const hasBalanceBeenRefreshed = params.get("hasBalanceBeenRefreshed");
		//we get data in the url named like this from payment provider
		const paymentInfo: PaymentInfo = {
			totalAmount: params.get("totalAmount"),
			currency: params.get("currency"),
			transactionId: params.get("TransactionID"),
			transactionDate: params.get("customField1"),
			paymentMethod: params.get("payment_method"),
			cardCompany: params.get("cardCompany"),
			status: params.get("ppp_status"),
			lugasInfoFields: params.get("fields"),
		};

		setTransactionDetails(paymentInfo);

		if (!hasBalanceBeenRefreshed && rootStore.userAuthStore.isLoggedIn) {
			//refresh user balance on transaction result, if user refreshes his page, we don't want to refresh his balance again
			rootStore.userAuthStore.refreshUserBalance();
			params.append("hasBalanceBeenRefreshed", "true");
			redirect(window.location.pathname + "?" + params.toString());

			const logFailedTransaction = () => {
				if (
					paymentInfo.status !== "OK" &&
					paymentInfo.status !== "PENDING"
				) {
					logger.logError(new Error("Deposit Transaction Failed"), {
						extra: {
							paymentInfo: paymentInfo,
							error: params.get("Error"),
							message: params.get("message"),
						},
					});
				}
			};

			logFailedTransaction();
		}
	}, []);

	return (
		<AdminLayoutWrapper>
			<div className="container">
				<div className="card--primary">
					<div className="card--primary__body">
						<TransactionStatusMessage
							transactionDetails={transactionDetails}
						/>

						<PaymentDetailsTable
							transactionDetails={transactionDetails}
						/>

						<Link
							to={`/${getCurrentCulture()}/${DefaultPath.HOME}`}
							className="at-accountActivationOk card--message__cta btn btn--base btn--accent u-mar--top--big"
						>
							{t("PAYMENT_INFO.HOME_PAGE")}
						</Link>
					</div>
				</div>
			</div>
		</AdminLayoutWrapper>
	);
});

function TransactionStatusMessage(props: { transactionDetails: PaymentInfo }) {
	const { t } = useTranslation();
	//status can be success, failure, pending
	const { status } = useParams<{ status: string }>();

	const { transactionDetails } = props;

	if (status === "success") {
		return (
			<>
				<div className="card--primary__header card--primary__header--icon">
					<i className="u-icon u-icon--xxxlrg u-icon--check-circle u-color--sent--success"></i>
					<div className="u-mar--top--xsml u-type--lrg">
						{t("PAYMENT_INFO.TRANSACTION_SUCCESS_TITLE")}
					</div>
				</div>

				<div className="u-type--base u-type--center u-mar--bottom--big">
					{t("PAYMENT_INFO.SUCCESSFUL_TRANSACTION")}
				</div>

				<PaymentLugasLimitInfoMessage
					transactionDetails={transactionDetails}
				/>
			</>
		);
	} else if (status === "pending") {
		return (
			<>
				<div className="card--primary__header card--primary__header--icon">
					<i className="u-icon u-icon--xxxlrg u-icon--pending"></i>
					<div className="u-mar--top--xsml u-type--lrg">
						{t("PAYMENT_INFO.TRANSACTION_PENDING_TITLE")}
					</div>
				</div>

				<div className="u-type--base u-type--center u-mar--bottom--big">
					{t("PAYMENT_INFO.TRANSACTION_PENDING_MESSAGE")}
				</div>

				<PaymentLugasLimitInfoMessage
					transactionDetails={transactionDetails}
				/>
			</>
		);
	}
	return (
		<>
			<div className="card--primary__header card--primary__header--icon">
				<i className="u-icon u-icon--xxxlrg u-icon--remove-circle u-color--sent--danger"></i>
				<div className="u-mar--top--xsml u-type--lrg">
					{t("PAYMENT_INFO.TRANSACTION_FAILED_TITLE")}
				</div>
			</div>

			<div className="u-type--base u-type--center u-mar--bottom--big">
				{t("PAYMENT_INFO.TRANSACTION_UNSUCCESSFUL_MESSAGE")}
			</div>

			<PaymentLugasLimitInfoMessage
				transactionDetails={transactionDetails}
			/>
		</>
	);
}

function PaymentLugasLimitInfoMessage(props: {
	transactionDetails: PaymentInfo;
}) {
	const {
		transactionDetails: { lugasInfoFields, currency, totalAmount },
	} = props;

	const { t } = useTranslation(["account-verification", "credit-rating"]);

	if (!lugasInfoFields) {
		return null;
	}

	//value fields are seperated by ;
	//key value pairs are seperated by ,
	const infoObject = lugasInfoFields?.split(";").reduce(
		(acc, field) => {
			const [key, stringifedValue] = field.split(",");

			const value =
				stringifedValue === "true" || stringifedValue === "false"
					? JSON.parse(stringifedValue)
					: stringifedValue;

			acc[key] = value;
			return acc;
		},
		{} as Record<string, string>
	);

	return (
		<>
			{infoObject.lugas && totalAmount && currency && (
				<div className="u-type--base u-type--center">
					{t("PAYMENT_INFO.LUGAS_LOWERED_DEPOSIT_MSG", {
						0: totalAmount,
						1: currency,
					})}
				</div>
			)}

			{infoObject?.limitRequestAmount &&
				infoObject?.limitRequestDate &&
				currency && (
					<div className="u-type--base u-type--center u-mar--bottom--big">
						{t("DEPOSIT.LUGAS_LIMIT.CROSS_LIMIT_REACHED", {
							interpolation: { escapeValue: false },
							0: infoObject.limitRequestAmount,
							1: currency,
							2: formatDateTime(infoObject.limitRequestDate),
						})}
					</div>
				)}

			{infoObject?.showCreditRating && (
				<div className="u-type--base u-type--center u-mar--bottom--big">
					{t("credit-rating:DESCRIPTION")}
				</div>
			)}

			{infoObject?.showVerificationLink && (
				<div className="u-type--base u-type--center u-mar--bottom--big">
					{t("ACC_VERIFICATION_NAVIGATION")}
				</div>
			)}

			{infoObject?.showCreditRating && (
				<div className="u-type--base u-type--center u-mar--bottom--big">
					{t("LIMIT_INCREASE.LIMIT_INCREASE_NAVIGATION")}
				</div>
			)}

			{(infoObject?.showCreditRating ||
				infoObject?.showVerificationLink) && (
				<div className="u-display--f u-display--f--justify-center u-gap--sml">
					{infoObject?.showVerificationLink && (
						<NavLink
							className="card--message__cta btn btn--sml btn--secondary u-mar--top--big"
							to={`/${getCurrentCulture()}/account-settings/account-verification`}
						>
							{t("TITLE")}
						</NavLink>
					)}
					{infoObject?.showCreditRating && (
						<NavLink
							className="card--message__cta btn btn--sml btn--secondary u-mar--top--big"
							to={`/${getCurrentCulture()}/limit-increase`}
						>
							{t("LIMIT_INCREASE.TITLE")}
						</NavLink>
					)}
				</div>
			)}
		</>
	);
}

function PaymentDetailsTable(props: { transactionDetails: PaymentInfo }) {
	const { t } = useTranslation();
	const { transactionDetails } = props;

	const formattedDateTime = DateTime.fromJSDate(
		new Date(transactionDetails.transactionDate + "Z")
	).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);

	const statusTagClasses = classNames(
		"u-align--v--middle u-type--case--capitalize",
		{
			"u-color--sent--success": transactionDetails.status === "OK",
			"u-color--text--primary": transactionDetails.status === "PENDING",
			"u-color--sent--danger": transactionDetails.status === "FAIL",
		}
	);

	const statusIconClasses = classNames(
		"u-icon u-icon--tny",
		{
			"u-icon--check u-color--sent--success":
				transactionDetails.status === "OK",
			"u-icon--clock": transactionDetails.status === "PENDING",
			"u-icon--remove u-color--sent--danger":
				transactionDetails.status === "FAIL",
		},
		"u-align--v--middle u-mar--right--xtny"
	);

	return (
		<table className="table--primary table--primary--bordered-row table--fixed u-mar--top--big">
			<thead className="table--primary__header table--primary__header--neutral">
				<tr className="table--primary__header__row">
					<th
						className="table--primary__header__data u-padd--xsml"
						colSpan={2}
					>
						{t("PAYMENT_INFO.DEPOSIT_DETAILS")}
					</th>
				</tr>
			</thead>
			<tbody className="table--primary__body">
				{transactionDetails.transactionId && (
					<tr className="table--primary__body__row">
						<td className="table--primary__body__data u-padd--xsml">
							{t("PAYMENT_INFO.TRANSACTION_ID")}
						</td>
						<td className="table--primary__body__data u-padd--xsml u-color--text--primary">
							{transactionDetails.transactionId}
						</td>
					</tr>
				)}

				<tr className="table--primary__body__row">
					<td className="table--primary__body__data u-padd--xsml">
						{t("PAYMENT_INFO.AMOUNT")}
					</td>
					<td className="table--primary__body__data u-padd--xsml u-color--text--primary">
						{transactionDetails.totalAmount}
					</td>
				</tr>
				<tr className="table--primary__body__row">
					<td className="table--primary__body__data u-padd--xsml">
						{t("PAYMENT_INFO.CURRENCY")}
					</td>
					<td className="table--primary__body__data u-padd--xsml u-color--text--primary">
						{transactionDetails.currency}
					</td>
				</tr>
				{transactionDetails?.cardCompany && (
					<tr className="table--primary__body__row">
						<td className="table--primary__body__data u-padd--xsml">
							{t("PAYMENT_INFO.PAYMENT_METHOD")}
						</td>
						<td className="table--primary__body__data u-padd--xsml u-color--text--primary">
							<div className="card--payment card--payment--borderless u-mar--bottom--reset">
								<div className="card--payment__area u-padd--reset">
									<span className="card--payment__icon">
										<i
											className={
												"card--payment__logo u-icon u-icon--payment u-icon--payment--base u-icon--payment--" +
												transactionDetails.cardCompany?.toLowerCase()
											}
										></i>
									</span>
									<span className="card--payment__details card--payment__details--centered">
										<span className="card--payment__name u-type--sml u-type--wgt--regular">
											{transactionDetails.cardCompany}
										</span>
										{/* <span className="card--payment__type u-type--xxsml">{transactionDetails.paymentMethod}</span> */}
									</span>
								</div>
							</div>
						</td>
					</tr>
				)}
				<tr className="table--primary__body__row">
					<td className="table--primary__body__data u-padd--xsml">
						{t("PAYMENT_INFO.STATUS")}
					</td>
					<td className="table--primary__body__data u-padd--xsml u-color--text--primary">
						<i className={statusIconClasses}></i>
						<span className={statusTagClasses}>
							{transactionDetails.status?.toLowerCase()}
						</span>
					</td>
				</tr>
				{transactionDetails.transactionDate && (
					<tr className="table--primary__body__row">
						<td className="table--primary__body__data u-padd--xsml">
							{t("PAYMENT_INFO.START_DATE")}
						</td>
						<td className="table--primary__body__data u-padd--xsml u-color--text--primary">
							{formattedDateTime}
						</td>
					</tr>
				)}
			</tbody>
		</table>
	);
}
